import { createContext, useContext, useEffect, useRef, useState } from 'react';
import { Accordion, AccordionPanel, AccordionTrigger } from '@backstage/ui';
import { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';
import {
  AccordionFieldFormData,
  AccordionFieldUiOptions,
} from './types';
import styles from './AccordionField.module.css';

const AccordionAncestorCollapsedContext = createContext(false);

export function orderProperties(
  properties: Record<string, object>,
  uiOrder?: string[],
): string[] {
  const keys = Object.keys(properties);
  if (!uiOrder || uiOrder.length === 0) return keys;

  const wildcardPos = uiOrder.indexOf('*');
  const explicitly = uiOrder.filter(k => k !== '*');
  const rest = keys.filter(k => !explicitly.includes(k));

  if (wildcardPos === -1) {
    return [...explicitly.filter(k => k in properties), ...rest];
  }

  const before = uiOrder.slice(0, wildcardPos).filter(k => k in properties);
  const after = uiOrder.slice(wildcardPos + 1).filter(k => k in properties);
  return [...before, ...rest, ...after];
}

export function hasNonEmptyErrors(schema: Record<string, any>): boolean {
  if (Array.isArray(schema.__errors) && schema.__errors.length > 0) return true;
  return Object.keys(schema)
    .filter(
      k =>
        k !== '__errors' &&
        schema[k] !== null &&
        typeof schema[k] === 'object',
    )
    .some(k => hasNonEmptyErrors(schema[k]));
}

export function mergeChildValue(
  current: Record<string, any>,
  key: string,
  value: unknown,
): Record<string, any> {
  const next = { ...current };
  if (value === undefined) {
    delete next[key];
  } else {
    next[key] = value;
  }
  return next;
}

export const AccordionField = (
  props: FieldExtensionComponentProps<
    AccordionFieldFormData | undefined,
    AccordionFieldUiOptions
  >,
) => {
  const {
    schema,
    uiSchema,
    idSchema,
    formData,
    errorSchema,
    onChange,
    onBlur,
    onFocus,
    registry,
    formContext,
    disabled,
    readonly,
  } = props;

  const accordionTitle =
    uiSchema?.['ui:options']?.accordionTitle ?? schema.title ?? 'Options';
  const defaultExpanded = uiSchema?.['ui:options']?.defaultExpanded ?? false;

  const [expanded, setExpanded] = useState<boolean>(defaultExpanded);

  const ancestorCollapsed = useContext(AccordionAncestorCollapsedContext);
  const effectivelyHidden = !expanded || ancestorCollapsed;

  const accumulatedRef = useRef<Record<string, any>>(formData ?? {});
  accumulatedRef.current = formData ?? {};

  const requiredFields = schema.required as string[] | undefined;

  useEffect(() => {
    const hasRequiredChildren =
      Array.isArray(requiredFields) && requiredFields.length > 0;
    if (
      hasRequiredChildren &&
      formData === undefined &&
      Object.keys(accumulatedRef.current).length === 0
    ) {
      onChange({});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (errorSchema !== undefined && hasNonEmptyErrors(errorSchema as any)) {
      setExpanded(true);
    }
  }, [errorSchema]);

  const { SchemaField } = registry.fields;
  const properties = schema.properties as Record<string, object> | undefined;
  const uiOrder = (uiSchema as any)?.['ui:order'] as string[] | undefined;
  const propertyKeys = properties ? orderProperties(properties, uiOrder) : [];
  const rootId = (idSchema as any)?.$id ?? 'root';

  return (
    <Accordion
      className={styles.accordion}
      isExpanded={expanded}
      onExpandedChange={setExpanded}
    >
      <AccordionTrigger
        id={`${rootId}-accordion-header`}
        title={accordionTitle}
      />
      <AccordionPanel id={`${rootId}-accordion-content`}>
        <AccordionAncestorCollapsedContext.Provider value={effectivelyHidden}>
          <div
            className={styles.details}
            style={expanded ? undefined : { display: 'none' }}
          >
            {propertyKeys.map(key => {
              const propertySchema = properties![key];
              const propertyUiSchema = (uiSchema as any)?.[key] ?? {};
              const propertyIdSchema = (idSchema as any)?.[key] ?? {
                $id: `${rootId}_${key}`,
              };
              const propertyErrorSchema = (errorSchema as any)?.[key] ?? {};

              return (
                <SchemaField
                  key={key}
                  name={key}
                  schema={propertySchema as any}
                  uiSchema={propertyUiSchema}
                  idSchema={propertyIdSchema}
                  formData={(formData as any)?.[key]}
                  errorSchema={propertyErrorSchema}
                  onChange={(value: any) => {
                    const next = mergeChildValue(
                      accumulatedRef.current,
                      key,
                      value,
                    );
                    accumulatedRef.current = next;
                    onChange(next);
                  }}
                  onBlur={onBlur}
                  onFocus={onFocus}
                  registry={registry}
                  formContext={formContext}
                  disabled={disabled}
                  readonly={readonly}
                  required={
                    !effectivelyHidden &&
                    (requiredFields?.includes(key) ?? false)
                  }
                  rawErrors={
                    Object.keys(propertyErrorSchema).length > 0
                      ? propertyErrorSchema.__errors ?? []
                      : []
                  }
                />
              );
            })}
          </div>
        </AccordionAncestorCollapsedContext.Provider>
      </AccordionPanel>
    </Accordion>
  );
};
